import pricingFeatures from './pricing-features.json' with { type: 'json' };

// Plan bullets are website copy (plan-card selling points). Plan facts
// (names, prices, seats, storage, AI tokens, workspaces) come from the
// canonical data/pricing-features.json shared with the Flutter app.
const usageMultipleOf = (plan: { slug: string; aiTokens: string }) => {
  const n = (v: string) => Number(String(v).replace(/[^0-9]/g, '')) || 0;
  const cadet = pricingFeatures.plans.find((p) => p.slug === 'cadet');
  const x = cadet ? Math.round(n(plan.aiTokens) / n(cadet.aiTokens)) : 1;
  return x <= 1 ? 'standard monthly usage' : `${x}× Cadet usage`;
};

const bulletsBySlug: Record<string, string[]> = {
  cadet: [
    "Invoices, proposals, clients, unlimited projects, tasks, notes, and calendar",
    "Email inbox, time tracking, intake forms, and files",
    "Email automations, up to 3 active workflows",
    "Good fit for solo operators getting organized",
  ],
  voyager: [
    "Shared inbox workflows, SMS, and calls",
    "Up to 10 active automation workflows, with SMS steps",
    "Dedicated SMS numbers require carrier compliance setup",
    "$10 per additional seat",
    "Built for small teams delivering client work together",
  ],
  specialist: [
    "IVR, call queues, full CRM, expenses, and basic accounting",
    "Up to 25 active automation workflows",
    "$10 per additional seat",
    "Adds payment plans, inventory, and deeper business workflows",
  ],
  commander: [
    "Full accounting, unlimited workflows with AI drafting, and advanced AI tools",
    "$10 per additional seat",
    "Dedicated support for larger teams",
    "Best for companies standardizing the whole operation",
  ],
  enterprise: [
    "Everything in Commander, plus 50 seats, 1 TB storage and 30× Cadet AI usage",
    "50 live funnels and 12,000 funnel leads a month; custom volume on request",
    "$10 per additional seat",
    "Dedicated support for larger teams",
    "For organizations standardizing multiple teams",
  ],
};

export const pricingPlans = pricingFeatures.plans.map((plan) => ({
  name: plan.name,
  slug: plan.slug,
  label: plan.audience,
  monthly: plan.monthly,
  yearlyMonthly: plan.yearlyMonthly,
  yearlyTotal: plan.yearlyTotal,
  value: `${plan.seats} ${plan.seats === 1 ? "seat" : "seats"}, ${usageMultipleOf(plan)}${plan.workspacesIncluded > 0 ? `, ${plan.workspacesIncluded} additional workspaces` : ""}`,
  seats: plan.seats,
  storage: plan.storage,
  aiTokens: plan.aiTokens,
  featured: plan.featured,
  bullets: bulletsBySlug[plan.slug],
}));

export const pricingDeals = [
  {
    title: "14-day trial",
    copy: "Try the tier you choose before the first bill.",
  },
  {
    title: "Save 20% yearly",
    copy: "Every plan has lower effective monthly pricing when billed annually.",
  },
  {
    title: "Nonprofit pricing",
    copy: "Eligible nonprofits can request discounted pricing from support.",
  },
  {
    title: "Flexible team growth",
    copy: "Voyager, Specialist, Commander, and Enterprise support paid add-on seats when the team grows.",
  },
  {
    title: "Carrier compliance setup",
    copy: "A $50 one-time carrier-mandated setup applies only when a workspace claims one or more dedicated SMS and voice numbers.",
  },
] as const;
